import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { jobsApi } from '../api/client'
import Button from '../components/ui/Button'
import DropZone from '../components/ui/DropZone'

/**
 * A7/A8: upload your own card images (fronts + optional custom backs) instead
 * of resolving against Scryfall — the web port of the desktop "Locals" tab.
 *
 * - Fronts: one image per card (order preserved).
 * - Backs: numbered custom backs; each front can be assigned one (or none —
 *   the back cell is left blank, keeping duplex alignment).
 * - Crop: apply the MPC bleed crop to every image, or per front.
 */
export default function LocalUpload() {
  const navigate = useNavigate()
  const [fronts, setFronts] = useState<File[]>([])
  const [backs, setBacks] = useState<File[]>([])
  // frontIndex -> backIndex (absent = no back)
  const [assignments, setAssignments] = useState<Record<number, number>>({})
  const [cropAll, setCropAll] = useState(false)
  const [cropPerFront, setCropPerFront] = useState<Record<number, boolean>>({})
  const [frontsOnly, setFrontsOnly] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const frontPreviews = useMemo(
    () => fronts.map((f) => URL.createObjectURL(f)),
    [fronts]
  )
  const backPreviews = useMemo(
    () => backs.map((b) => URL.createObjectURL(b)),
    [backs]
  )

  const onFrontsFiles = (files: File[]) => {
    setFronts(files)
    setAssignments({})
    setCropPerFront({})
  }

  const onBacksFiles = (files: File[]) => {
    setBacks(files)
    setAssignments({})
  }

  const setBackFor = (frontIdx: number, value: string) => {
    setAssignments((prev) => {
      const next = { ...prev }
      if (value === '') delete next[frontIdx]
      else next[frontIdx] = Number(value)
      return next
    })
  }

  const toggleCropFor = (frontIdx: number) => {
    setCropPerFront((prev) => ({ ...prev, [frontIdx]: !prev[frontIdx] }))
  }

  const handleSubmit = async () => {
    setError(null)
    if (fronts.length === 0) {
      setError('Sube al menos una imagen frontal.')
      return
    }
    setSubmitting(true)
    try {
      const crop: 'all' | 'none' | boolean[] = cropAll
        ? 'all'
        : fronts.map((_, i) => Boolean(cropPerFront[i]))
      const job = await jobsApi.createLocal(
        fronts,
        backs,
        assignments,
        crop,
        'mtg',
        frontsOnly
      )
      navigate(`/job/${job.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear el trabajo')
      setSubmitting(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-gray-900">Subir imágenes locales</h1>
        <p className="text-gray-600">
          Usa tus propias imágenes de cartas (frontales y reversos personalizados)
          sin buscar en Scryfall. Opcionalmente aplica el recorte de borde MPC.
        </p>
      </div>

      {/* Fronts */}
      <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-3">
        <h2 className="text-lg font-semibold">1. Frontales ({fronts.length})</h2>
        <DropZone
          onFiles={onFrontsFiles}
          accept="image/*"
          multiple
          icon="🖼️"
          label="Arrastra las imágenes frontales"
          hint="Una imagen por carta, en orden"
        />
      </section>

      {/* Backs */}
      <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-3">
        <h2 className="text-lg font-semibold">2. Reversos personalizados ({backs.length})</h2>
        <p className="text-sm text-gray-500">
          Opcional. Sube uno o varios reversos; luego asígnalos a cada frontal.
        </p>
        <DropZone
          onFiles={onBacksFiles}
          accept="image/*"
          multiple
          icon="🔄"
          label="Arrastra los reversos"
          hint="Opcional: uno o varios reversos para asignar"
        />
        {backs.length > 0 && (
          <div className="flex flex-wrap gap-3 pt-2">
            {backs.map((b, i) => (
              <div key={i} className="text-center">
                <img
                  src={backPreviews[i]}
                  alt={b.name}
                  className="w-16 h-24 object-cover rounded border border-gray-300"
                />
                <div className="text-xs text-gray-500 mt-1">Reverso {i}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Options */}
      <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        <h2 className="text-lg font-semibold">3. Opciones</h2>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={cropAll}
            onChange={(e) => setCropAll(e.target.checked)}
          />
          Recortar borde MPC en todas las imágenes
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={frontsOnly}
            onChange={(e) => setFrontsOnly(e.target.checked)}
          />
          Solo frontales (no generar página de reversos)
        </label>
      </section>

      {/* Per-card assignment */}
      {fronts.length > 0 && (
        <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-3">
          <h2 className="text-lg font-semibold">4. Asignación por carta</h2>
          <div className="space-y-3">
            {fronts.map((f, i) => (
              <div
                key={i}
                className="flex items-center gap-4 border-b border-gray-100 pb-3 last:border-0"
              >
                <img
                  src={frontPreviews[i]}
                  alt={f.name}
                  className="w-14 h-20 object-cover rounded border border-gray-300"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-800 truncate">{f.name}</div>
                  {!cropAll && (
                    <label className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                      <input
                        type="checkbox"
                        checked={Boolean(cropPerFront[i])}
                        onChange={() => toggleCropFor(i)}
                      />
                      Recortar borde MPC
                    </label>
                  )}
                </div>
                {backs.length > 0 && !frontsOnly && (
                  <select
                    value={assignments[i] ?? ''}
                    onChange={(e) => setBackFor(i, e.target.value)}
                    className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
                  >
                    <option value="">Sin reverso</option>
                    {backs.map((_, bi) => (
                      <option key={bi} value={bi}>
                        Reverso {bi}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <Button size="lg" onClick={handleSubmit} disabled={submitting || fronts.length === 0}>
          {submitting ? 'Creando…' : 'Generar PDF'}
        </Button>
      </div>
    </div>
  )
}
